// src/utils/timezone.js
//
// SIMPLIFIED: NO TIMEZONE CONVERSION
//
// The database stores values as-is. The frontend receives them as-is.
// No conversion, no adjustment, no timezone math.
// Simply parse the string and display it exactly as received.
//
// Rules:
// 1. Parse strings as local time (the way they are stored)
// 2. Never add or subtract hours
// 3. Never use timeZone parameter in formatting
// 4. Pass through datetime-local inputs directly to API

/**
 * Parse a datetime coming from the API into a JS Date.
 * No timezone conversion - treat as literal local time.
 *
 * Handles:
 *   "2026-03-09 09:51:55"      <- what the API sends
 *   "2026-03-09T09:51:55"      <- ISO without a zone
 *   Date objects               <- passed straight through
 */
export const parseServerTime = (value) => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;

  if (typeof value === 'string') {
    // "YYYY-MM-DD HH:MM:SS" or "YYYY-MM-DDTHH:MM:SS"
    // Parse as local time (no timezone conversion)
    const match = value.match(
      /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/
    );
    if (match) {
      const [, y, mo, d, h, mi, s] = match;
      return new Date(
        Number(y), Number(mo) - 1, Number(d),
        Number(h), Number(mi), Number(s || 0)
      );
    }

    // Date-only: "2026-03-09"
    const dateOnly = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnly) {
      const [, y, mo, d] = dateOnly;
      return new Date(Number(y), Number(mo) - 1, Number(d));
    }
  }

  const fallback = new Date(value);
  return isNaN(fallback.getTime()) ? null : fallback;
};

// Alias for backwards compatibility
export const parseNairobiTime = parseServerTime;

/**
 * Format a server datetime for display.
 * No timezone conversion - display exactly as stored.
 * @param {string|Date} value
 * @param {'full'|'datetime'|'date'|'time'|'short'} format
 */
export const formatDateTime = (value, format = 'datetime') => {
  const date = parseServerTime(value);
  if (!date) return 'N/A';

  // NOTE: No timeZone parameter - display as-is
  switch (format) {
    case 'time':
      return date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });

    case 'date':
      return date.toLocaleDateString('en-GB', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });

    case 'short':
      return date.toLocaleDateString('en-GB', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });

    case 'full':
      return date.toLocaleString('en-GB', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });

    case 'datetime':
    default:
      return date.toLocaleString('en-GB', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
  }
};

/**
 * Current time as reported by device (no adjustment)
 */
export const nowLocal = () => new Date();

/** Milliseconds until `value`. Negative once it has passed. */
export const msUntil = (value) => {
  const date = parseServerTime(value);
  if (!date) return 0;
  return date.getTime() - Date.now();
};

/** Milliseconds since `value`. Negative if it is in the future. */
export const msSince = (value) => {
  const date = parseServerTime(value);
  if (!date) return 0;
  return Date.now() - date.getTime();
};

/** "3 days remaining" / "42 minutes remaining" / "Expired" */
export const getRemainingTime = (value) => {
  if (!value) return 'No expiry';

  const diffMs = msUntil(value);
  if (diffMs <= 0) return 'Expired';

  const mins = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMs / 3600000);
  const days = Math.floor(diffMs / 86400000);

  if (mins < 1) return 'Less than a minute remaining';
  if (mins < 60) return `${mins} minute${mins !== 1 ? 's' : ''} remaining`;
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} remaining`;
  return `${days} day${days !== 1 ? 's' : ''} remaining`;
};

/**
 * Countdown parts for CountdownTimer / AuctionCard.
 * @returns {{days,hours,minutes,seconds,total,expired}}
 */
export const getCountdownParts = (value) => {
  const total = msUntil(value);
  if (total <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, total: 0, expired: true };
  }
  return {
    days: Math.floor(total / 86400000),
    hours: Math.floor((total % 86400000) / 3600000),
    minutes: Math.floor((total % 3600000) / 60000),
    seconds: Math.floor((total % 60000) / 1000),
    total,
    expired: false,
  };
};

/** "Just now" / "12 min ago" / "3 days ago" */
export const formatTimeAgo = (value) => {
  if (!value) return 'Just now';

  const diffMs = msSince(value);
  if (diffMs < 0) return 'Just now'; // clock skew

  const mins = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMs / 3600000);
  const days = Math.floor(diffMs / 86400000);

  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  if (days <= 7) return `${days} day${days > 1 ? 's' : ''} ago`;
  return formatDateTime(value, 'date');
};

/** Current clock, e.g. "14:07" -- for headers and the burner widget. */
export const getCurrentTime = () =>
  new Date().toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

/**
 * Value for <input type="datetime-local">, i.e. "YYYY-MM-DDTHH:MM".
 * No conversion - what the form shows is what the DB stores.
 */
export const toInputValue = (value) => {
  const date = parseServerTime(value);
  if (!date) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
};

/**
 * Turn an <input type="datetime-local"> value back into the
 * "YYYY-MM-DD HH:MM:SS" string the API expects.
 * No conversion - pass through as-is.
 */
export const fromInputValue = (inputValue) => {
  if (!inputValue) return null;
  const [datePart, timePart = '00:00'] = inputValue.split('T');
  const [hh = '00', mm = '00', ss = '00'] = timePart.split(':');
  return `${datePart} ${hh}:${mm}:${ss.slice(0, 2)}`;
};

// Legacy alias
export const getCurrentNairobiTime = () => new Date();
